// Performance string parser for song parts.
//
// One rule: anything wrapped in square brackets is a chord, everything
// else is plain text. Chords and text live on the same line — the parser
// splits each line into a sequence of segments, each tagged chord or text,
// and the renderer styles them differently while keeping them inline.
//
// The brackets themselves are replaced with spaces so a chord's column
// position in the source string is preserved in the rendered output.
//
//      [Em]              [G]
// Now she's walking through the clouds

export type SegmentType = 'chord' | 'text'

export interface PerformanceSegment {
  type: SegmentType
  text: string
}

export interface PerformanceLine {
  segments: PerformanceSegment[] // empty array = blank line
}

const CHORD_RE = /\[([^\]]+)\]/g

function parseLine(raw: string): PerformanceLine {
  if (raw === '') return { segments: [] }
  const segments: PerformanceSegment[] = []
  let last = 0
  CHORD_RE.lastIndex = 0
  let m: RegExpExecArray | null
  while ((m = CHORD_RE.exec(raw)) !== null) {
    if (m.index > last) {
      segments.push({ type: 'text', text: raw.slice(last, m.index) })
    }
    // Chord name starts at the column where '[' was typed (shifted 1 left
    // from the old "bracket → space" scheme). Both bracket chars become
    // trailing spaces so the overall column width of the chord is
    // preserved: `[Em]` (4 cols) → `Em  ` (4 cols).
    segments.push({ type: 'chord', text: m[1] ?? '' })
    segments.push({ type: 'text', text: '  ' })
    last = CHORD_RE.lastIndex
  }
  if (last < raw.length) {
    segments.push({ type: 'text', text: raw.slice(last) })
  }
  return { segments }
}

export function parsePerformance(perf: string | null | undefined): PerformanceLine[] {
  if (!perf) return []
  return perf.split('\n').map(parseLine)
}

// Longest visible column across all lines, useful for sizing.
export function maxLineWidth(lines: PerformanceLine[]): number {
  return lines.reduce((m, l) => {
    const w = l.segments.reduce((s, seg) => s + seg.text.length, 0)
    return Math.max(m, w)
  }, 0)
}

export function isBlankLine(line: PerformanceLine): boolean {
  return line.segments.length === 0
}
